import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { PayStationFormSchemaValues } from 'src/features/payment/utils/formSchema';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { PAY_STATION_UI_MODAL, PayStationPaymentType, PIPELINE_PROFILE } from 'src/features/payment/utils/types';
import { LOCALE } from 'src/providers/types';
import InputFormField from 'src/registry/components/forms/InputFormField';
import MultiSelectFormField from 'src/registry/components/forms/MultiSelectFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Separator } from 'src/registry/ui/Separator';
import { generalMessages } from 'src/registry/utils/messages';
import { Site } from 'src/features/sites/utils/types';
import { CURRENCY } from 'src/types';
import { BOOLEAN_MAP, BOOLEAN_OPTIONS } from 'src/features/sites/utils/constants';
import { payStationUiModelTranslationMap, pipelineProfileTranslationMap } from 'src/features/payment/utils/constants';
import { gatewayMessages } from 'src/features/gateway/utils/messages';
import { useListGateways } from 'src/features/gateway/api/useListGateways';
import { useParking } from 'src/providers/ParkingProvider';
import { ICON_MAP } from 'src/utils/constants';

type PayStationFormProps = {
    form: UseFormReturn<PayStationFormSchemaValues>;
    onSubmit: (values: PayStationFormSchemaValues) => void;
    siteOptions: Site[];
};

const languageTranslationMap = {
    [LOCALE.BG]: paymentsMessages.bulgarian,
    [LOCALE.EN]: paymentsMessages.english,
};

const NOMINAL_PLACEHOLDER = {
    currency: undefined as unknown as CURRENCY,
    enable: undefined as unknown as boolean,
    value: '',
};

enum NOMINAL_NAME {
    COIN_ACCEPT = 'coin_selector.accepted_nominals',
    COIN_PAYOUT = 'coin_selector.payout_nominals',
    BANKNOTE_ACCEPT = 'banknote_validator.accepted_nominals',
    BANKNOTE_PAYOUT = 'banknote_validator.payout_nominals',
}
function PayStationForm({ onSubmit, form, siteOptions }: PayStationFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { data: gateways } = useListGateways({ parkingId });

    const {
        settings: {
            banknote_validator,
            coin_selector,
            languages,
            default_language,
            enabled_payment_methods,
            currencies,
        },
        ui_mode,
    } = form.watch();

    React.useEffect(() => {
        if (form.formState.isDirty) {
            if (ui_mode !== PAY_STATION_UI_MODAL.INTERACTIVE) {
                form.setValue('settings.enable_settings_enter', null);
                form.setValue('settings.enable_subscriptions', null);
                form.setValue('settings.enable_free_stay', null);
                form.setValue('settings.lost_ticket', null);
                form.setValue('settings.password', null);
                form.setValue('settings.languages', null);
                form.setValue('settings.default_language', null);
                form.setValue('settings.web_id', null);
                form.setValue('settings.home_qr_value', null);
                form.setValue('settings.support_phone', null);
                form.setValue('settings.home_pay_button_text', null);
            }
            if (ui_mode === PAY_STATION_UI_MODAL.INTERACTIVE) {
                form.setValue('gateway', null);
            }
        }
    }, [ui_mode, form]);

    const currencyArray = [
        {
            id: CURRENCY.EUR,
            name: CURRENCY.EUR,
        },
        {
            id: CURRENCY.BGN,
            name: CURRENCY.BGN,
        },
    ];
    const currency_map = currencyArray.filter((c) => currencies?.includes(c.id));

    const { languageOptions, currencyOptions, enablePaymentMethods, nominalMap } = React.useMemo(() => {
        return {
            languageOptions: [
                {
                    id: LOCALE.BG,
                    name: formatMessage(paymentsMessages.bulgarian),
                },
                {
                    id: LOCALE.EN,
                    name: formatMessage(paymentsMessages.english),
                },
            ],
            currencyOptions: currency_map,
            enablePaymentMethods: [
                {
                    id: PayStationPaymentType.CARD,
                    name: formatMessage(paymentsMessages.card),
                },
                ...(ui_mode === PAY_STATION_UI_MODAL.INTERACTIVE
                    ? [
                          {
                              id: PayStationPaymentType.CASH,
                              name: formatMessage(paymentsMessages.cash),
                          },
                      ]
                    : []),
                {
                    id: PayStationPaymentType.ONLINE,
                    name: formatMessage(paymentsMessages.online),
                },
            ],
            nominalMap: [
                {
                    value: coin_selector?.accepted_nominals,
                    name: NOMINAL_NAME.COIN_ACCEPT,
                    label: formatMessage(paymentsMessages.coinAcceptedNominals),
                    addLabel: formatMessage(paymentsMessages.addCoinAcceptedNominals),
                },
                {
                    value: coin_selector?.payout_nominals,
                    name: NOMINAL_NAME.COIN_PAYOUT,
                    label: formatMessage(paymentsMessages.coinPayoutNominals),
                    addLabel: formatMessage(paymentsMessages.addCoinPayoutNominals),
                },
                {
                    value: banknote_validator?.accepted_nominals,
                    name: NOMINAL_NAME.BANKNOTE_ACCEPT,
                    label: formatMessage(paymentsMessages.banknoteAcceptedNominals),
                    addLabel: formatMessage(paymentsMessages.addBanknoteAcceptedNominals),
                },
                {
                    value: banknote_validator?.payout_nominals,
                    name: NOMINAL_NAME.BANKNOTE_PAYOUT,
                    label: formatMessage(paymentsMessages.banknotePayoutNominals),
                    addLabel: formatMessage(paymentsMessages.addBanknotePayoutNominals),
                },
            ],
        };
    }, [
        formatMessage,
        coin_selector?.accepted_nominals,
        coin_selector?.payout_nominals,
        banknote_validator?.accepted_nominals,
        banknote_validator?.payout_nominals,
        ui_mode,
        currency_map,
    ]);

    const defaultLanguageOptions = languageOptions
        .filter((option) => languages?.includes(option.id))
        .map((option) => option.id);

    React.useEffect(() => {
        if (default_language && languages?.length && !languages.includes(default_language)) {
            form.setValue('settings.default_language', null);
        }
    }, [languages, default_language, form]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<PayStationFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(generalMessages.name)}
            />
            <InputFormField<PayStationFormSchemaValues>
                form={form}
                name="code"
                label={formatMessage(paymentsMessages.code)}
            />
            <FormField
                control={form.control}
                name="site"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...paymentsMessages.site} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={`${formatMessage(paymentsMessages.site)}...`} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {siteOptions.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {
                                                    option.translations.find(
                                                        (translation) => translation.language === locale,
                                                    )?.name
                                                }
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <SelectFormField<PayStationFormSchemaValues>
                form={form}
                name="ui_mode"
                label={formatMessage(paymentsMessages.uiModel)}
                options={Object.values(PAY_STATION_UI_MODAL)}
                optionTranslationMap={payStationUiModelTranslationMap}
                required={true}
            />
            <InputFormField<PayStationFormSchemaValues>
                form={form}
                name="backend_url"
                label={formatMessage(paymentsMessages.backendUrl)}
            />
            <InputFormField<PayStationFormSchemaValues>
                form={form}
                name="backend_api_key"
                label={formatMessage(paymentsMessages.backendApiKey)}
            />
            {ui_mode && ui_mode !== PAY_STATION_UI_MODAL.INTERACTIVE && (
                <FormField
                    control={form.control}
                    name="gateway"
                    render={({ field }) => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                <FormattedMessage {...gatewayMessages.gateway} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value ?? ''}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder={`${formatMessage(gatewayMessages.gateway)}...`} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {gateways?.results.map((option) => (
                                                <SelectItem key={option.id} value={option.id}>
                                                    {
                                                        option.translations.find((option) => option.language === locale)
                                                            ?.name
                                                    }
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            )}

            <Separator className="mt-2 mb-4" />

            <SelectFormField<PayStationFormSchemaValues>
                form={form}
                name="settings.pipeline_profile"
                label={formatMessage(paymentsMessages.pipelineProfile)}
                options={Object.values(PIPELINE_PROFILE)}
                optionTranslationMap={pipelineProfileTranslationMap}
                required={true}
            />

            <MultiSelectFormField<PayStationFormSchemaValues>
                form={form}
                options={enablePaymentMethods}
                name="settings.enabled_payment_methods"
                label={formatMessage(paymentsMessages.enablePaymentMethods)}
            />

            <MultiSelectFormField<PayStationFormSchemaValues>
                form={form}
                options={currencyArray}
                name="settings.currencies"
                label={formatMessage(paymentsMessages.currencies)}
            />
            {ui_mode && ui_mode === PAY_STATION_UI_MODAL.INTERACTIVE && (
                <>
                    <InputFormField<PayStationFormSchemaValues>
                        form={form}
                        name="settings.password"
                        label={formatMessage(paymentsMessages.servicePassword)}
                    />
                    <MultiSelectFormField<PayStationFormSchemaValues>
                        form={form}
                        options={languageOptions}
                        name="settings.languages"
                        label={formatMessage(paymentsMessages.languages)}
                    />
                    <SelectFormField<PayStationFormSchemaValues>
                        form={form}
                        optionTranslationMap={languageTranslationMap}
                        name="settings.default_language"
                        label={formatMessage(paymentsMessages.defaultLanguage)}
                        options={defaultLanguageOptions}
                    />
                    <SelectFormField<PayStationFormSchemaValues>
                        form={form}
                        name="settings.lost_ticket"
                        label={formatMessage(paymentsMessages.lostTicket)}
                        options={BOOLEAN_OPTIONS}
                        optionTranslationMap={BOOLEAN_MAP}
                        isBoolean
                    />
                    <SelectFormField<PayStationFormSchemaValues>
                        form={form}
                        name="settings.enable_free_stay"
                        label={formatMessage(paymentsMessages.enableFreeStay)}
                        options={BOOLEAN_OPTIONS}
                        optionTranslationMap={BOOLEAN_MAP}
                        isBoolean
                    />

                    <SelectFormField<PayStationFormSchemaValues>
                        form={form}
                        name="settings.enable_subscriptions"
                        label={formatMessage(paymentsMessages.enableSubscription)}
                        options={BOOLEAN_OPTIONS}
                        optionTranslationMap={BOOLEAN_MAP}
                        isBoolean
                    />
                    <SelectFormField<PayStationFormSchemaValues>
                        form={form}
                        name="settings.enable_settings_enter"
                        label={formatMessage(paymentsMessages.enableSettingsEnter)}
                        options={BOOLEAN_OPTIONS}
                        optionTranslationMap={BOOLEAN_MAP}
                        isBoolean
                    />

                    <InputFormField<PayStationFormSchemaValues>
                        form={form}
                        name="settings.web_id"
                        label={formatMessage(paymentsMessages.webId)}
                        required={false}
                    />

                    <InputFormField<PayStationFormSchemaValues>
                        form={form}
                        name="settings.home_qr_value"
                        label={formatMessage(paymentsMessages.homeQR)}
                        required={false}
                    />

                    <InputFormField<PayStationFormSchemaValues>
                        form={form}
                        name="settings.support_phone"
                        pattern={/^(?:\+?[0-9]+)?$/}
                        type="tel"
                        label={formatMessage(paymentsMessages.supportPhone)}
                        required={false}
                    />

                    {languages?.includes(LOCALE.BG) && (
                        <InputFormField<PayStationFormSchemaValues>
                            form={form}
                            name="settings.home_pay_button_text.bg"
                            label={formatMessage(paymentsMessages.homePayTextBG)}
                            required={false}
                        />
                    )}

                    {languages?.includes(LOCALE.EN) && (
                        <InputFormField<PayStationFormSchemaValues>
                            form={form}
                            name="settings.home_pay_button_text.en"
                            label={formatMessage(paymentsMessages.homePayTextEN)}
                            required={false}
                        />
                    )}
                </>
            )}
            {enabled_payment_methods?.includes(PayStationPaymentType.CASH) && (
                <>
                    {nominalMap.map(({ value, name, label, addLabel }) => {
                        return (
                            <>
                                <Separator className="mt-2 mb-4" />
                                {value.map((_, index) => (
                                    <>
                                        <FormItem variant="dialog">
                                            <FormLabel variant="dialog" required>
                                                {label}
                                            </FormLabel>
                                            <div key={index} className="flex">
                                                <div className="grid w-full grid-cols-3">
                                                    <InputFormField<PayStationFormSchemaValues>
                                                        form={form}
                                                        name={`settings.${name}.${index}.value`}
                                                        placeholder={`${formatMessage(paymentsMessages.amount)}...`}
                                                    />
                                                    <FormField
                                                        control={form.control}
                                                        name={`settings.${name}.${index}.currency`}
                                                        render={({ field }) => {
                                                            return (
                                                                <FormItem variant="dialog">
                                                                    <FormControlStack>
                                                                        <FormControl>
                                                                            <Select
                                                                                onValueChange={field.onChange}
                                                                                value={field.value}
                                                                            >
                                                                                <SelectTrigger className="w-full">
                                                                                    <SelectValue
                                                                                        placeholder={formatMessage(
                                                                                            paymentsMessages.currency,
                                                                                        )}
                                                                                    />
                                                                                </SelectTrigger>
                                                                                <SelectContent>
                                                                                    {currencyOptions.map(({ name }) => (
                                                                                        <SelectItem
                                                                                            key={name}
                                                                                            value={name}
                                                                                        >
                                                                                            {name}
                                                                                        </SelectItem>
                                                                                    ))}
                                                                                </SelectContent>
                                                                            </Select>
                                                                        </FormControl>
                                                                        <FormMessage />
                                                                    </FormControlStack>
                                                                </FormItem>
                                                            );
                                                        }}
                                                    />
                                                    <FormField
                                                        control={form.control}
                                                        name={`settings.${name}.${index}.enable`}
                                                        render={({ field }) => {
                                                            return (
                                                                <FormItem variant="dialog">
                                                                    <FormControlStack>
                                                                        <FormControl>
                                                                            <Select
                                                                                onValueChange={(value) =>
                                                                                    field.onChange(JSON.parse(value))
                                                                                }
                                                                                value={field.value?.toString()}
                                                                            >
                                                                                <SelectTrigger className="w-full">
                                                                                    <SelectValue
                                                                                        className="truncate"
                                                                                        placeholder={formatMessage(
                                                                                            paymentsMessages.enable,
                                                                                        )}
                                                                                    />
                                                                                </SelectTrigger>
                                                                                <SelectContent>
                                                                                    {BOOLEAN_OPTIONS.map((option) => (
                                                                                        <SelectItem
                                                                                            key={option}
                                                                                            value={option}
                                                                                        >
                                                                                            {formatMessage(
                                                                                                BOOLEAN_MAP[
                                                                                                    option as 'true'
                                                                                                ],
                                                                                            )}
                                                                                        </SelectItem>
                                                                                    ))}
                                                                                </SelectContent>
                                                                            </Select>
                                                                        </FormControl>
                                                                        <FormMessage />
                                                                    </FormControlStack>
                                                                </FormItem>
                                                            );
                                                        }}
                                                    />
                                                </div>
                                                <Button
                                                    onClick={() => {
                                                        const newLimits = value.filter(
                                                            (_, innerIndex) => innerIndex !== index,
                                                        );
                                                        form.setValue(`settings.${name}`, newLimits);
                                                    }}
                                                    variant="destructiveLight"
                                                    size="icon"
                                                    className="shrink-0"
                                                    type="button"
                                                >
                                                    <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                                </Button>
                                            </div>
                                        </FormItem>
                                    </>
                                ))}
                                <div className="flex flex-col gap-2 md:flex-row md:gap-4">
                                    <div className="md:w-36" />
                                    <Button
                                        variant="outline"
                                        type="button"
                                        className="flex-1"
                                        onClick={() =>
                                            form.setValue(`settings.${name}`, [...value, NOMINAL_PLACEHOLDER])
                                        }
                                    >
                                        <FontAwesomeIcon className="text-primary-700" icon={ICON_MAP.ADD} />
                                        {addLabel}
                                    </Button>
                                </div>
                            </>
                        );
                    })}
                </>
            )}
        </Form>
    );
}

export default PayStationForm;
